import React, { useState, useEffect, useRef, useCallback } from "react";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { parseMediaUrl } from "../utils/mediaHelper";
import { FaVolumeMute, FaVolumeUp } from "react-icons/fa";
import "./Hero.css";

const DEFAULT_SLIDE_DURATION = 8000; // 8 detik default jika belum diset

const Hero = () => {
  const [heroContent, setHeroContent] = useState({
    title: "SMK Pariwisata Sumbangsih",
    tagline: "",
    mediaItems: [],
    slideDuration: DEFAULT_SLIDE_DURATION
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  // State untuk fungsionalitas drag/swipe
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const slideIntervalRef = useRef(null);

  // Toggle audio untuk video YouTube dan direct video
  const toggleSound = () => {
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);

    // Kirim perintah unMute/mute ke iframe YouTube aktif via postMessage
    const activeIframe = document.querySelector('.hero-slide:nth-child(' + (currentIndex + 1) + ') .hero-iframe-media');
    if (activeIframe && activeIframe.contentWindow) {
      activeIframe.contentWindow.postMessage(
        JSON.stringify({
          event: 'command',
          func: nextMuted ? 'mute' : 'unMute',
          args: []
        }),
        '*'
      );
    }

    // Toggle suara untuk direct video
    const activeDirectVideo = document.querySelector('.hero-slide:nth-child(' + (currentIndex + 1) + ') video');
    if (activeDirectVideo) {
      activeDirectVideo.muted = nextMuted;
    }
  };

  // Hook untuk mengambil data dari Firestore
  useEffect(() => {
    const fetchHeroData = async () => {
      setLoading(true);
      try {
        const settingsSnap = await getDoc(doc(db, "settings", "siteConfig"));
        const pageContentSnap = await getDoc(doc(db, "pageContent", "homePage"));

        setHeroContent(prevContent => {
          const newContent = { ...prevContent };

          if (settingsSnap.exists()) {
            const settingsData = settingsSnap.data();
            newContent.title = `${settingsData.siteName || "SMK Pariwisata Sumbangsih"}`;
            newContent.tagline = settingsData.tagline || "";
          }

          if (pageContentSnap.exists()) {
            const pageData = pageContentSnap.data();
            const firestoreImages = pageData.heroImages;
            
            // Set durasi slide otomatis (dalam milidetik)
            if (pageData.heroSlideDuration) {
              newContent.slideDuration = Math.max(2000, Number(pageData.heroSlideDuration) * 1000);
            }

            if (Array.isArray(firestoreImages) && firestoreImages.length > 0) {
              newContent.mediaItems = firestoreImages.map((img, idx) => {
                const url = typeof img === "string" ? img : img.url;
                const explicitType = typeof img === "object" ? img.mediaType : null;
                const parsed = parseMediaUrl(url, explicitType);
                return {
                  id: (typeof img === "object" && img.id) ? img.id : `hero_slide_${idx}`,
                  url: url,
                  type: explicitType || parsed.type,
                  duration: (typeof img === "object" && img.duration) ? Number(img.duration) * 1000 : null,
                  directUrl: parsed.directUrl,
                  driveId: parsed.driveId,
                  previewUrl: parsed.previewUrl,
                  streamUrl: parsed.streamUrl,
                  imageUrl: parsed.imageUrl,
                  youtubeId: parsed.youtubeId,
                  embedUrl: parsed.embedUrl
                };
              });
            }
          }

          return newContent;
        });
      } catch (error) {
        console.error("Error fetching hero content:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchHeroData();
  }, []);

  // Fungsi transisi ke slide berikutnya saat video selesai
  const goToNextSlide = useCallback(() => {
    if (heroContent.mediaItems.length <= 1) return;
    setCurrentIndex(prevIndex => (prevIndex + 1) % heroContent.mediaItems.length);
  }, [heroContent.mediaItems.length]);

  // Listener untuk pesan postMessage dari YouTube Player API saat video selesai (event onStateChange = 0)
  useEffect(() => {
    const handleYouTubeMessage = (event) => {
      try {
        if (!event.data) return;
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        // event.info === 0 menandakan video YouTube telah selesai (ENDED)
        if (data && (data.event === 'onStateChange' || data.info !== undefined) && data.info === 0) {
          goToNextSlide();
        }
      } catch (e) {}
    };

    window.addEventListener('message', handleYouTubeMessage);
    return () => window.removeEventListener('message', handleYouTubeMessage);
  }, [goToNextSlide]);

  // Hook untuk slider otomatis yang dinamis per-slide
  useEffect(() => {
    if (heroContent.mediaItems.length <= 1) return;

    if (isDragging) {
      clearTimeout(slideIntervalRef.current);
      return;
    }

    const currentItem = heroContent.mediaItems[currentIndex];
    const isDirectVideo = currentItem && (currentItem.type === 'video' || (currentItem.url && currentItem.url.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i)));
    const isYouTube = currentItem && (currentItem.type === 'youtube' || (currentItem.url && (currentItem.url.includes('youtube.com') || currentItem.url.includes('youtu.be'))));

    // Jika slide adalah video (Direct Video atau YouTube), biarkan video berputar sampai selesai
    // Transisi akan dipicu secara akurat oleh event onEnded (video) atau onStateChange (YouTube).
    // Kecuali jika user secara eksplisit menyetel durasi kustom di Admin Dashboard (currentItem.duration).
    if (currentItem && currentItem.duration) {
      slideIntervalRef.current = setTimeout(() => {
        goToNextSlide();
      }, currentItem.duration);
    } else if (!isDirectVideo && !isYouTube) {
      // Untuk gambar/slide biasa, gunakan durasi timer
      const slideDuration = heroContent.slideDuration || DEFAULT_SLIDE_DURATION;
      slideIntervalRef.current = setTimeout(() => {
        goToNextSlide();
      }, slideDuration);
    }

    return () => clearTimeout(slideIntervalRef.current);
  }, [currentIndex, heroContent.mediaItems, heroContent.slideDuration, isDragging, goToNextSlide]);

  const handleDragStart = (e) => {
    setIsDragging(true);
    const clientX = e.touches ? e.touches[0].clientX : e.pageX;
    setStartX(clientX || 0);
    setDragOffset(0);
    clearTimeout(slideIntervalRef.current);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    const clientX = e.touches ? e.touches[0].clientX : e.pageX;
    if (clientX === undefined) return;
    setDragOffset(startX - clientX);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const threshold = 40;
    if (dragOffset > threshold) {
      setCurrentIndex(prevIndex => (prevIndex + 1) % heroContent.mediaItems.length);
    } else if (dragOffset < -threshold) {
      setCurrentIndex(prevIndex => (prevIndex - 1 + heroContent.mediaItems.length) % heroContent.mediaItems.length);
    }

    setDragOffset(0);
  };

  return (
    <header className="hero-section">
      <div 
        className="hero-slider-container"
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
        onTouchCancel={handleDragEnd}
      >
        {loading ? (
          <div className="hero-loader-container">
            <div className="hero-loader"></div>
          </div>
        ) : heroContent.mediaItems.length > 0 ? (
          <>
            <div 
              className="hero-slider" 
              style={{ 
                transform: `translateX(calc(-${currentIndex * 100}% - ${dragOffset}px))`,
                transition: isDragging ? 'none' : 'transform 0.8s cubic-bezier(0.45, 0.05, 0.55, 0.95)'
              }}
            >
              {heroContent.mediaItems.map((item, index) => {
                const isDirectVideo = item.type === 'video' || (item.url && item.url.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i));
                const isYouTube = item.type === 'youtube' || (item.url && (item.url.includes('youtube.com') || item.url.includes('youtu.be')));
                const isDrive = item.type === 'drive' || item.type === 'drive_video' || (item.url && (item.url.includes('drive.google.com') || item.url.includes('docs.google.com')));
                
                // Optimasi performa: hanya load iframe/video jika slide sedang aktif atau 1 slide di dekatnya
                const isNearActive = Math.abs(currentIndex - index) <= 1 || 
                  (currentIndex === 0 && index === heroContent.mediaItems.length - 1) ||
                  (currentIndex === heroContent.mediaItems.length - 1 && index === 0);
                const isActive = index === currentIndex;

                return (
                  <div key={item.id || index} className="hero-slide">
                    {isDirectVideo ? (
                      isNearActive ? (
                        <video
                          ref={(el) => {
                            if (el && isActive) {
                              if (el.paused) {
                                el.play().catch(() => {});
                              }
                            }
                          }}
                          src={item.directUrl || item.url}
                          autoPlay
                          muted
                          playsInline
                          webkit-playsinline="true"
                          preload="auto"
                          className="hero-media-element"
                          onLoadedMetadata={(e) => {
                            // Jika durasi video terdeteksi dari metadata file, update durasi otomatis
                            if (e.target.duration && !item.duration) {
                              item.duration = e.target.duration * 1000;
                            }
                            if (isActive && e.target.paused) {
                              e.target.play().catch(() => {});
                            }
                          }}
                          onEnded={() => {
                            goToNextSlide();
                          }}
                        />
                      ) : null
                    ) : isYouTube ? (
                      /* YouTube Background Video Player: Hanya muat iframe jika slide aktif agar tidak membebani browser */
                      <div className="hero-iframe-video-wrapper">
                        {isActive ? (
                          <iframe
                            src={item.embedUrl || `https://www.youtube-nocookie.com/embed/${item.youtubeId}?autoplay=1&mute=1&controls=0&playsinline=1&rel=0&modestbranding=1&iv_load_policy=3&disablekb=1&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`}
                            title={`Hero Video Slide ${index + 1}`}
                            className="hero-iframe-media"
                            allow="autoplay; encrypted-media; picture-in-picture"
                            tabIndex="-1"
                          />
                        ) : (
                          /* Thumbnail placeholder saat tidak aktif */
                          <img
                            src={item.thumbnailUrl || `https://img.youtube.com/vi/${item.youtubeId}/maxresdefault.jpg`}
                            alt={`Slide ${index + 1}`}
                            className="hero-media-element"
                            loading="lazy"
                          />
                        )}
                      </div>
                    ) : isDrive ? (
                      /* Google Drive Video Player: Hanya render iframe jika slide aktif agar tidak ada multiple heavy Google Drive clients */
                      <div className="hero-drive-video-wrapper">
                        {isActive ? (
                          <iframe
                            src={`https://drive.google.com/file/d/${item.driveId}/preview?autoplay=1`}
                            title={`Google Drive Slide ${index + 1}`}
                            className="hero-drive-iframe"
                            allow="autoplay; encrypted-media; fullscreen"
                          />
                        ) : (
                          /* Thumbnail placeholder hemat resource dari Google Drive saat idle */
                          <img
                            src={item.imageUrl || `https://lh3.googleusercontent.com/d/${item.driveId}`}
                            alt={`Slide ${index + 1}`}
                            className="hero-media-element"
                            loading="lazy"
                          />
                        )}
                      </div>
                    ) : (
                      /* Regular image / gif / cloudinary dengan background blur estetik */
                      <div className="hero-image-container">
                        <img
                          src={item.directUrl || item.url}
                          alt=""
                          aria-hidden="true"
                          className="hero-media-blur-bg"
                        />
                        <img
                          src={item.directUrl || item.url}
                          alt={`Slide ${index + 1}`}
                          className="hero-media-element"
                          loading={index === 0 ? "eager" : "lazy"}
                        />
                      </div>
                    )}
                    <div className="hero-overlay" />
                  </div>
                );
              })}
            </div>

            <div className="hero-dots">
              {heroContent.mediaItems.map((_, index) => (
                <button
                  key={index}
                  className={`hero-dot ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            {/* Tombol Suara (Mute / Unmute) Khusus Slide Video */}
            {heroContent.mediaItems[currentIndex] && (
              heroContent.mediaItems[currentIndex].type === 'youtube' ||
              heroContent.mediaItems[currentIndex].type === 'video'
            ) && (
              <button
                type="button"
                className="hero-sound-toggle-btn"
                onClick={toggleSound}
                title={isMuted ? "Bunyikan Video (Unmute)" : "Senyapkan Video (Mute)"}
                aria-label={isMuted ? "Unmute sound" : "Mute sound"}
              >
                {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                <span className="hero-sound-label">{isMuted ? "Suara Mati" : "Suara Aktif"}</span>
              </button>
            )}
          </>
        ) : (
          <div className="hero-slide no-image-fallback" />
        )}
      </div>
    </header>
  );
};

export default Hero;
